import { Component, EventEmitter, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { TableBase } from '../../shared/components/table/table-base';
import { ActionEvent } from '../../shared/components/table/table.component';
import { NetworkCountry } from '../../shared/models/network.model';
import { LocalStorageService } from '../../shared/services/localStorage.service';
import { DialogRef, ModalService } from '../../shared/services/modal.service';
import { AllRequestParamsRegion, NetInfoService } from '../../shared/services/net-info.service';
import { NotificationService } from '../../shared/services/notification.service';

@Component({
  standalone: false,
  selector: 'app-country-table',
  templateUrl: './country-table.component.html',
  styleUrls: ['./country-table.component.scss'],
})
export class CountryTableComponent extends TableBase implements OnInit {
  @Output() actions = new EventEmitter<ActionEvent>();
  @Output() onAfterDelete = new EventEmitter<void>();

  @ViewChild('deleteModalTpl', { read: TemplateRef, static: false }) deleteModalTpl: any;
  @ViewChild('formModalTpl', { read: TemplateRef, static: false }) formModalTpl: any;
  formModal: DialogRef;
  countryModel: NetworkCountry;

  requestParams = new AllRequestParamsRegion();
  allRows = [];

  constructor(
    public notificationService: NotificationService,
    public service: NetInfoService,
    public storage: LocalStorageService,
    public modal: ModalService
  ) {
    super();
  }

  ngOnInit() {
    this.loading = true;
    this.setColumns(
      this.createColumns([
        { title: 'Country name', prop: 'name' },
        { title: 'ISO', prop: 'isoAlpha2' },
        { title: 'Phone number prefixes', prop: 'prefixes' },
        { title: 'Language', prop: 'languageName' },
      ])
    );
    this.tableActions = [
      { icon: 'icon-edit', name: 'edit' },
      { icon: 'far fa-trash-alt', name: 'delete' },
    ];
    this.requestParams.setSort('name', 'asc');
    this.load();
  }

  update() {
    const search = this.requestParams.search ? this.requestParams.search.toLowerCase().trim() : '';
    const rows = this.allRows.filter((country: NetworkCountry) => {
      if (search) {
        return country.name.toLowerCase().indexOf(search) !== -1;
      }
      return true;
    });
    this.setData(this.createRows(rows), rows.length);
  }

  onCreate() {
    this.countryModel = this.service.createCountry();
    this.formModal = this.modal.alert().component(this.formModalTpl).open();
  }

  onAfterSave() {
    this.load();
  }

  load() {
    this.loading = true;
    for (const sort of this.order) {
      this.requestParams.setSort(sort.prop, sort.direction);
    }
    this.service.countries(this.requestParams).subscribe({
      next: (collection: NetworkCountry[]) => {
        this.allRows = collection;
        this.update();
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.notificationService.error({
          title: 'Countries',
          message: 'An error occurred while loading the countries',
          serviceName: 'NET-INTEL',
          requestMessage: error.statusText,
          requestCode: error.status,
        });
      },
      complete: () => {
        if (this.formModal) {
          this.formModal.close();
        }
      },
    });
  }

  onAction(event: ActionEvent) {
    if (event.name === 'delete') {
      const dialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.deleteModalTpl).open();
      dialogRef.result.then(result => {
        if (result) {
          this.delete(event.row.data);
        }
      });
    }

    if (event.name === 'edit') {
      this.countryModel = event.row.data;
      this.formModal = this.modal.alert().component(this.formModalTpl).open();
    }

    this.actions.emit(event);
  }

  delete(country: NetworkCountry) {
    this.loading = true;
    this.service.deleteCountry(country.id).subscribe({
      next: () => {
        this.notificationService.success('Country deleted', 'Countries');
        this.onAfterDelete.emit();
        this.load();
      },
      error: error => {
        this.loading = false;
        this.notificationService.error({
          title: 'Countries',
          message: 'An error occurred while deleting the country',
          serviceName: 'NET-INTEL',
          requestMessage: error.statusText,
          requestCode: error.status,
        });
      },
    });
  }
}
